/**
 * Which chunks to hold, which to ask for, and which to let go.
 *
 * ⚠️ **This is the file that decides whether 103,000 km² is buildable.** The
 * first version loaded a ring around the player and never unloaded anything, so
 * walking far enough grew memory without bound. On the reference machine — 4 GB
 * of VRAM, not the 16 the brief assumed — that is minutes, not hours.
 *
 * Kept pure and free of `three` and the DOM so the decisions can be tested
 * without a browser, a GPU or a server. The arithmetic is where the bugs are.
 */

import { chunkOrigin, type ChunkCoord } from '@lofi/shared';

/** How many chunks out from the player to load, horizontally. */
export const MIN_VIEW_RADIUS = 2;
export const MAX_VIEW_RADIUS = 24;
export const DEFAULT_VIEW_RADIUS = 8;

/**
 * ⚠️ How much further than the load radius a chunk may sit before it is
 * dropped.
 *
 * Without a margin, a player standing exactly on a chunk boundary loads and
 * unloads the same ring every time they step back and forth — which costs a
 * mesh rebuild each way and looks like flickering. Two chunks of slack means
 * you have to genuinely leave before anything is thrown away.
 */
export const UNLOAD_HYSTERESIS = 2;

export function clampViewRadius(radius: number): number {
  if (!Number.isFinite(radius)) return DEFAULT_VIEW_RADIUS;
  return Math.max(MIN_VIEW_RADIUS, Math.min(MAX_VIEW_RADIUS, Math.round(radius)));
}

/** Horizontal distance between two chunks, in chunks. Vertical is ignored. */
export function chunkDistance(a: ChunkCoord, b: ChunkCoord): number {
  return Math.hypot(a.cx - b.cx, a.cz - b.cz);
}

/**
 * The chunks that should be loaded around a centre, nearest first.
 *
 * Nearest first matters: the ground under your feet must arrive before the
 * horizon, or you spawn into a hole while distant scenery loads.
 *
 * ## 🔴 And within a column, the layer you are IN comes before the rock — T215
 *
 * 🎯 **haugur, 2026-09-15:** *"i also notice when i fly that the ground below me
 * is being drawn from top layer to botton layer at the same time, one would
 * think it would be enough to strat by drawing the surface layer first… no need
 * to draw what i dont see."*
 *
 * 🔴 **He was right, and the order was not merely un-prioritised — it was
 * BACKWARDS.** `chunkDistance` ignores the vertical entirely and `Array.sort`
 * is stable, so ties kept the order this function built them in: the inner loop
 * runs `cy = cyMin` upward, **so the two layers of buried rock under your feet
 * were asked for BEFORE the surface layer you are standing on.**
 *
 * ✅ **Ties now break on how far the layer is from your own**, so the surface
 * arrives first and the rock last.
 *
 * ## ⚠️ Why the layers are REORDERED and not DROPPED
 *
 * 🎯 **haugur asked for the stronger version** — *"if a player stops or lands
 * the undelying layers would be drawn in"* — **and this deliberately stops
 * short of it.** 🔴 **The reason is T204.** He flew over Gullfoss and asked
 * *"Why is there a hole there"*: the band asked for one layer below the player,
 * and at 221 m with the riverbed at 144 m **the ground was two layers down and
 * was never requested. You could see the sky through the world.**
 *
 * **So "the layer below me is rock I cannot see" is true when you are standing
 * and false when you are flying**, which is the case he was describing — and
 * the client cannot tell which without knowing where the ground is, **which it
 * does not know until the chunk arrives.** ⚠️ **Dropping a layer on a guess
 * re-opens a hole he personally reported.**
 *
 * 🔴 **And most of the reason to drop them went away the same day**: T216 made
 * every layer of a column share one terrain build, so the buried layers now
 * cost the SERVER almost nothing. What they still cost is **bandwidth** (T185)
 * and **a mesh build on the player's main thread** — and both of those have
 * their own fix that does not involve guessing.
 *
 * 👉 **Reordering is free and cannot hurt. Dropping needs the server to say
 * where the ground is** — a per-chunk heightmap, which is what Minecraft stores
 * for exactly this (`Development/Minecraft/03_The_World.md`, §1).
 */
export function chunksWanted(
  centre: ChunkCoord,
  radius: number,
  cyMin: number,
  cyMax: number,
  inArea: (coord: ChunkCoord) => boolean,
): ChunkCoord[] {
  const wanted: ChunkCoord[] = [];

  for (let dx = -radius; dx <= radius; dx++) {
    for (let dz = -radius; dz <= radius; dz++) {
      /* A square ring would load the corners, which are further away than the
         radius and are the first thing fog hides anyway. */
      if (Math.hypot(dx, dz) > radius) continue;

      for (let cy = cyMin; cy <= cyMax; cy++) {
        const coord = { cx: centre.cx + dx, cy, cz: centre.cz + dz };
        if (inArea(coord)) wanted.push(coord);
      }
    }
  }

  return wanted.sort((a, b) => {
    /* Horizontal first — unchanged, and it is the one that matters most. */
    const byDistance = chunkDistance(a, centre) - chunkDistance(b, centre);
    if (byDistance !== 0) return byDistance;

    /* 🔴 T215. Ties break on how far the layer is from the player's own, so the
       surface arrives before the rock. ⚠️ This used to be left to `sort`'s
       stability, which meant the BOTTOM layer of every column went first. */
    return Math.abs(a.cy - centre.cy) - Math.abs(b.cy - centre.cy);
  });
}

/**
 * 🔴 **How many chunk layers BELOW the player's own to stream.**
 *
 * 🎯 **haugur, flying over Gullfoss on 2026-09-14:** *"Why is there a hole
 * there"* — and, from the same spot, *"and here"*. ⚠️ **They were holes straight
 * through the world, showing the SKY**, in the middle of the river.
 *
 * 🔴 **It was `-1`, and that is one layer.** Flying at **221.6 m** puts you in
 * layer 6, so the lowest block the client asked for was **y = 160** — and a
 * pocket in the river bed at **144.3 m** had nothing requested in it at all.
 * **Not "drawn dark" — nothing, so you see the sky through the ground.**
 *
 * ⚠️ **T199 recorded this as a known cost and described it as *"a valley floor
 * 300 m below a clifftop"*. It is not 300 m — it bit at 58 m**, which is an
 * ordinary height to fly at, and it will happen over any canyon, fjord or coast.
 * 🔴 **A limit that reads as a hole in the world is a bug, however deliberate
 * the limit was.**
 *
 * ⚠️ **Widening costs bandwidth**: five layers instead of four is **25% more
 * chunks**, on a world stream that is still sent **uncompressed** (T185).
 * 🎯 **haugur chose to pay it** rather than take the adaptive or the
 * shift-the-band alternatives, 2026-09-14.
 */
export const CY_BELOW = -2;

/**
 * How many chunk layers ABOVE the player's own to stream.
 *
 * ⚠️ **Unchanged.** Two layers is what lets a hill or a cliff above you draw,
 * and the fault being fixed was entirely below.
 */
export const CY_ABOVE = 2;

/** How many layers the band is, and therefore how many metres tall it is. */
export const BAND_LAYERS = CY_ABOVE - CY_BELOW + 1;

/**
 * 🔴 **The vertical band of chunk layers to stream around a player.**
 *
 * ⚠️ **This used to be two constants, and they were ABSOLUTE.** The client asked
 * for y −32 m to +96 m wherever you stood, which was invisible while the world
 * was 1,400 m of coast at 13 m — and made **most of Iceland unreachable** the
 * day the world became the island.
 *
 * 🎯 **haugur, at Vatnajökull, 2026-09-14:** *"There is nothing here, i cant
 * break anything, nothing is within reach, sky and i"* — at **−65,938 m**, still
 * falling, because the ground at 1,465 m was never requested.
 *
 * 👉 **Centred on the player, clamped to the world's real floor and ceiling.**
 * Pure arithmetic, here rather than in `main.ts`, because this file's whole
 * argument is that the arithmetic is where the bugs are — **and this one shipped
 * because nothing could test it.**
 */
export function verticalSpan(
  centreCy: number,
  below: number,
  above: number,
  worldCyMin: number,
  worldCyMax: number,
): { cyMin: number; cyMax: number } {
  return {
    cyMin: Math.max(worldCyMin, centreCy + below),
    cyMax: Math.min(worldCyMax, centreCy + above),
  };
}

/** Has this chunk fallen far enough behind to be thrown away? */
export function shouldUnload(coord: ChunkCoord, centre: ChunkCoord, radius: number): boolean {
  return chunkDistance(coord, centre) > radius + UNLOAD_HYSTERESIS;
}

/**
 * Rectangular bounds in world coordinates, inclusive of the minimum edge and
 * exclusive of the maximum — the usual half-open convention, so adjacent areas
 * tile without overlapping.
 */
export interface WorldBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

/**
 * Does any part of this chunk fall inside the bounds?
 *
 * Any part, not the origin: a chunk straddling the edge holds ground the player
 * can stand on, and cutting it would put a 32 m cliff around the test area.
 */
export function chunkIntersects(coord: ChunkCoord, bounds: WorldBounds, size: number): boolean {
  const origin = chunkOrigin(coord);
  return (
    origin.x + size > bounds.minX &&
    origin.x < bounds.maxX &&
    origin.z + size > bounds.minZ &&
    origin.z < bounds.maxZ
  );
}

/** How many chunk columns a bounds covers — for saying how big the world is. */
export function chunkColumnsIn(bounds: WorldBounds, size: number): number {
  const across = Math.ceil(bounds.maxX / size) - Math.floor(bounds.minX / size);
  const down = Math.ceil(bounds.maxZ / size) - Math.floor(bounds.minZ / size);
  return across * down;
}
